import React, { Component } from 'react';
import PropTypes from 'prop-types';

import IdentityAvatarContainer from '../../../containers/global/identity/IdentityAvatarContainer';
import IdentityNameContainer from '../../../containers/global/identity/IdentityNameContainer';
import CustomControl from '../../global/CustomControl';
import {
  DISCOVER_PURCHASE_SELECTION_MODAL,
  PURCHASE_IDENTITY_NAV_MODAL,
  UPDATE_ACCOUNT_IDENTITY_MODAL,
} from '../../../constants/modals';
import { AVATAR_SIZE_52 } from '../../../constants/avatar-sizes';
import EditIcon from '../../icons/EditIcon';
import Badge from '../../global/Badge';
import {
  isAlbumFolderTagSelectionRequired,
  isAlbumFolderGradeTagSelectionRequired,
} from '../../../apps/temp/views/onboarding/TagSelection/helpers';
import { IDENTITY_TAG_SELECTION_MODAL } from '../../../apps/temp/views/onboarding/TagSelection/components/IdentityTagSelection/modal';
import { GRADE_TAG_SELECTION_MODAL } from '../../../apps/temp/views/onboarding/TagSelection/components/GradeTagSelection/modal';
import { ALBUM_IDENTITY_ACCESS } from '../../../constants/identity';
import { getDefaultJoinType } from '../../../util/discover-album-folder';
import { isPrepaidOnlyAlbumFolder } from '../../../util/album-folder';

class AlbumFolderPurchaseSelectAccountIdentity extends Component {
  static propTypes = {
    identity: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      current_photo: PropTypes.object,
      has_outdated_selfie: PropTypes.bool,
    }),
    isSelected: PropTypes.bool,
    isPurchased: PropTypes.bool,
    onSelectIdentity: PropTypes.func.isRequired,
    onDeselectIdentity: PropTypes.func.isRequired,
    uploadedPhoto: PropTypes.object,
    openModal: PropTypes.func.isRequired,
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    isJoined: PropTypes.bool,
    allowsJoinTypeSelection: PropTypes.bool.isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }),
  };

  onEditIdentity = event => {
    event.preventDefault();
    event.stopPropagation();

    const { identity, uploadedPhoto, openModal } = this.props;

    openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
      identity,
      forceSelfieUpdate:
        (!identity.current_photo || identity.has_outdated_selfie) &&
        !uploadedPhoto,
      outdatedSelfie: identity.has_outdated_selfie && !uploadedPhoto,
    });
  };

  toggleIdentity(identity, event) {
    event.preventDefault();

    const {
      onSelectIdentity,
      setActiveIdentity,
      onDeselectIdentity,
      uploadedPhoto,
      openModal,
      isPurchased,
      isSelected,
      albumFolder,
      allowsJoinTypeSelection,
      history,
    } = this.props;

    if (isPurchased) {
      return;
    }

    if (!identity.current_photo && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        forceSelfieUpdate: true,
      });
      return;
    }

    // `updateSelfie` request is not sync in nature, i.e.
    // the field identity.has_outdated_selfie will return `true`
    // even if we update the selfie, so to check if a user
    // really has an outdated selfie we also need to check
    // our local redux store for recent uploads for this identity,
    // if there's an `uploadedPhoto` it means that the user just
    // uploaded a selfie (which by definition is not considered outdated)
    if (identity.has_outdated_selfie && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        outdatedSelfie: true,
      });
      return;
    }

    if (!identity.last_name) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
      });
      return;
    }

    if (isSelected) {
      onDeselectIdentity(identity);
    } else {
      onSelectIdentity(
        {
          ...identity,
          selectedAlbums: [],
          albumFolders: [albumFolder],
        },
        getDefaultJoinType(albumFolder),
      );
      setActiveIdentity(identity);

      const onSelectionCompleted = () => {
        // Use this so we can easily add SUBTYPES to show the modal to, if needed.
        const identityModalSubtypes = [];
        const isPrimaryIdentityOnly =
          albumFolder.album_identity_access ===
          ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY;

        if (
          identityModalSubtypes.includes(albumFolder.album_subtype) &&
          !isPrimaryIdentityOnly
        ) {
          openModal(PURCHASE_IDENTITY_NAV_MODAL);
        } else if (isPrepaidOnlyAlbumFolder(albumFolder)) {
          history.replace(
            `/purchase/album-folder/${albumFolder.id}/pre-purchase`,
          );
        } else {
          history.replace(
            `/purchase/album-folder/${albumFolder.id}/purchase-summary`,
          );
        }
      };

      if (allowsJoinTypeSelection) {
        openModal(DISCOVER_PURCHASE_SELECTION_MODAL);
      } else if (isAlbumFolderGradeTagSelectionRequired(albumFolder)) {
        openModal(GRADE_TAG_SELECTION_MODAL, {
          onComplete: () => {
            if (isAlbumFolderTagSelectionRequired(albumFolder)) {
              openModal(IDENTITY_TAG_SELECTION_MODAL, {
                onComplete: onSelectionCompleted,
              });

              return;
            }

            onSelectionCompleted();
          },
        });
      } else if (isAlbumFolderTagSelectionRequired(albumFolder)) {
        openModal(IDENTITY_TAG_SELECTION_MODAL, {
          onComplete: onSelectionCompleted,
        });
      } else {
        onSelectionCompleted();
      }
    }
  }

  render() {
    const { identity, isSelected, isPurchased, isJoined } = this.props;

    return (
      <a
        href="#"
        className="identity-container"
        data-testid="identity-container"
        onClick={event => this.toggleIdentity(identity, event)}
      >
        <IdentityAvatarContainer
          identity={identity}
          className="mr-2"
          size={AVATAR_SIZE_52}
          onClick={this.onEditIdentity}
        >
          <span className="btn-identity-edit">
            <EditIcon color="#0d84fe" />
          </span>
        </IdentityAvatarContainer>

        <IdentityNameContainer
          className="text-dark mr-1"
          identity={identity}
          defaultName="Find photos of me"
        />
        {!isPurchased && isJoined && (
          <span className="text-primary">(Joined)</span>
        )}
        {isPurchased && <Badge className="ml-auto">Subscribed</Badge>}
        {!isPurchased && (
          <div className="ml-auto">
            <CustomControl
              id={identity.id}
              type="checkbox"
              size="xl"
              checked={isSelected}
              readOnly
            />
          </div>
        )}
      </a>
    );
  }
}

export default AlbumFolderPurchaseSelectAccountIdentity;
